import type { ErrorsResult } from '@auth0/auth0-acul-react/types';
import { useEffect, useMemo } from 'react';

import { trackError } from '@/lib/tagging';

/**
 * Tracks all Auth0 errors via GTM tagging and returns display-ready error data.
 *
 * @param errors - The errors API from Auth0 ACUL SDK `useErrors()`
 * @param trackingPath - The tagging path for error events (e.g. 'mfa/sms/error')
 * @returns generalErrorMessages - array of non-field error messages for AuthErrorBanner display
 * @returns fieldErrors - record mapping field names to their first error message
 */
export const useTrackErrors = (errors: ErrorsResult, trackingPath: string) => {
  const allErrors = errors.byType('auth0');

  useEffect(() => {
    for (const error of allErrors) {
      trackError(trackingPath, error.code);
    }
  }, [allErrors, trackingPath]);

  const generalErrorMessages = allErrors
    .filter((err) => !err.field)
    .map((e) => e.message);

  const fieldErrors = useMemo(() => {
    const map: Record<string, string> = {};
    for (const err of allErrors) {
      if (err.field && !map[err.field]) {
        map[err.field] = err.message;
      }
    }
    return map;
  }, [allErrors]);

  return { generalErrorMessages, fieldErrors };
};
